import type { FederatedPointerEvent } from "pixi.js";
import type { CustomViewport } from "../CustomViewport";
import { HOVER_LONG_PRESS_MS, supportsHover } from "../interaction";

export interface InteractionTarget {
    onClick?: () => void;
    onHoverChange?: (hovered: boolean) => void;
}

interface PointerIdentity {
    type: string;
    id: number;
}

interface PointerState {
    navigationGenerationAtDown: number | null;
    releaseObserved: boolean;
    clickSuppressed: boolean;
}

interface PointerTapOptions {
    stopPropagation?: boolean;
}

interface HoverSession {
    target: InteractionTarget;
    pointer: PointerIdentity;
    capturedOnHoverChange?: (hovered: boolean) => void;
}

interface HoverSessionFilter {
    target?: InteractionTarget;
    pointer?: PointerIdentity;
}

interface PendingHoverState {
    type: "pending";
    session: HoverSession;
    timer: number;
}

interface ActiveHoverState {
    type: "active";
    session: HoverSession;
}

type HoverState = PendingHoverState | ActiveHoverState | null;

const getPointerIdentity = (event: FederatedPointerEvent): PointerIdentity => ({
    type: event.pointerType,
    id: event.pointerId,
});

const getPointerKey = (pointer: PointerIdentity): string =>
    `${pointer.type}:${pointer.id}`;

const samePointer = (
    first: PointerIdentity,
    second: PointerIdentity,
): boolean => first.type === second.type && first.id === second.id;

export default class InteractionManager {
    private viewport: CustomViewport | null = null;
    private navigationGeneration = 0;
    private readonly pointerStates = new Map<string, PointerState>();
    private hoverState: HoverState = null;

    public connect(viewport: CustomViewport): void {
        if (this.viewport === viewport) return;
        if (this.viewport) this.disconnect(this.viewport);

        this.viewport = viewport;

        // Capture observes every gesture start before a child or viewport plugin can handle it.
        viewport.addEventListener("pointerdown", this.handlePointerDown, true);
        // Normally propagated release events use capture so child handlers cannot hide
        // the manager's pointer lifecycle update.
        viewport.addEventListener("pointerup", this.handlePointerRelease, true);
        viewport.addEventListener(
            "pointercancel",
            this.handlePointerRelease,
            true,
        );
        // Pixi emits pointerupoutside by directly notifying the original press path in
        // the bubbling phase, so a capture listener does not observe it.
        viewport.addEventListener(
            "pointerupoutside",
            this.handlePointerRelease,
        );
        // Pixi notifies pointerleave at AT_TARGET for every ancestor, which includes
        // capture listeners on the viewport.
        viewport.addEventListener(
            "pointerleave",
            this.handlePointerRelease,
            true,
        );
        viewport.on("navigation-start", this.handleNavigationStart);
        viewport.on("navigation-end", this.handleNavigationEnd);
        viewport.on("camera-move-start", this.handleCameraMoveStart);
        viewport.on("viewport-destroy", this.handleViewportDestroy);
    }

    public disconnect(viewport: CustomViewport): void {
        if (this.viewport !== viewport) return;

        viewport.removeEventListener(
            "pointerdown",
            this.handlePointerDown,
            true,
        );
        viewport.removeEventListener(
            "pointerup",
            this.handlePointerRelease,
            true,
        );
        viewport.removeEventListener(
            "pointercancel",
            this.handlePointerRelease,
            true,
        );
        viewport.removeEventListener(
            "pointerupoutside",
            this.handlePointerRelease,
        );
        viewport.removeEventListener(
            "pointerleave",
            this.handlePointerRelease,
            true,
        );
        viewport.off("navigation-start", this.handleNavigationStart);
        viewport.off("navigation-end", this.handleNavigationEnd);
        viewport.off("camera-move-start", this.handleCameraMoveStart);
        viewport.off("viewport-destroy", this.handleViewportDestroy);

        this.viewport = null;
        this.navigationGeneration += 1;
        this.pointerStates.clear();
        this.clearHover();
    }

    public destroy(): void {
        const viewport = this.viewport;
        if (viewport) {
            this.disconnect(viewport);
            return;
        }

        this.pointerStates.clear();
        this.clearHover();
    }

    public pointerOver(
        event: FederatedPointerEvent,
        target: InteractionTarget,
    ): void {
        // Snapshot the adapter closure instead of retaining Pixi's pooled event.
        // The closure keeps this session's domain event; adapters may still read their
        // latest public consumer callbacks when the captured closure eventually runs.
        const session = this.createHoverSession(event, target);
        const { pointer } = session;

        if (supportsHover(pointer.type)) {
            this.startImmediateHover(session);
            return;
        }

        const viewport = this.viewport;
        const activePointer = this.activeHoverPointer;
        if (
            !viewport?.isUsable ||
            viewport.isNavigating ||
            (activePointer !== null && !supportsHover(activePointer.type))
        ) {
            return;
        }

        const pointerState = this.ensurePointerState(pointer);
        this.startLongPress(session, pointerState);
    }

    public pointerMove(
        event: FederatedPointerEvent,
        target: InteractionTarget,
    ): void {
        if (!supportsHover(event.pointerType)) return;

        this.startImmediateHover(this.createHoverSession(event, target));
    }

    public pointerOut(
        event: FederatedPointerEvent,
        target?: InteractionTarget,
    ): void {
        const pointer = getPointerIdentity(event);
        const filter = { target, pointer };
        const shouldDeferTouchHoverEnd =
            !supportsHover(pointer.type) && this.isActiveHover(filter);

        if (!shouldDeferTouchHoverEnd) {
            this.clearHover(filter);
            return;
        }

        // Touch pointerout may arrive before drag/pinch state settles. Defer one
        // microtask so viewport navigation can claim the gesture first.
        queueMicrotask(() => {
            if (!this.viewport?.isNavigating) this.clearHover(filter);
        });
    }

    public pointerTap(
        event: FederatedPointerEvent,
        target: InteractionTarget,
        options: PointerTapOptions = {},
    ): boolean {
        const pointer = getPointerIdentity(event);
        const pointerState = this.pointerStates.get(getPointerKey(pointer));

        this.cancelPendingHover({ pointer });

        if (!this.canTriggerClick(pointerState)) return false;

        if (!target.onClick) return false;

        if (options.stopPropagation !== false) event.stopPropagation();
        target.onClick();
        return true;
    }

    public removeTarget(target: InteractionTarget): void {
        this.clearHover({ target });
    }

    private readonly handlePointerDown = (
        event: FederatedPointerEvent,
    ): void => {
        const pointer = getPointerIdentity(event);
        const pointerKey = getPointerKey(pointer);
        const existing = this.pointerStates.get(pointerKey);

        // Touch pointerover can precede pointerdown. Keep the state object referenced by
        // its long-press timer, but capture gesture validity at the actual pointer start.
        if (existing && existing.navigationGenerationAtDown === null) {
            existing.navigationGenerationAtDown = this.navigationGeneration;
            existing.releaseObserved = false;
            existing.clickSuppressed = false;
            return;
        }

        this.pointerStates.set(pointerKey, {
            navigationGenerationAtDown: this.navigationGeneration,
            releaseObserved: false,
            clickSuppressed: false,
        });
    };

    private readonly handlePointerRelease = (
        event: FederatedPointerEvent,
    ): void => {
        const pointer = getPointerIdentity(event);
        const pointerKey = getPointerKey(pointer);
        const pointerState = this.pointerStates.get(pointerKey);
        if (!pointerState) return;

        pointerState.releaseObserved = true;

        // Pixi emits pointertap during pointerup dispatch, before queued microtasks.
        queueMicrotask(() => {
            if (
                this.pointerStates.get(pointerKey) !== pointerState ||
                !pointerState.releaseObserved ||
                this.viewport?.isNavigating
            ) {
                return;
            }

            if (!supportsHover(pointer.type)) this.clearHover({ pointer });
            this.pointerStates.delete(pointerKey);
        });
    };

    private readonly handleNavigationStart = (): void => {
        this.navigationGeneration += 1;
        this.cancelPendingHover();
    };

    private readonly handleNavigationEnd = (): void => {
        const activePointer = this.activeHoverPointer;
        if (activePointer && !supportsHover(activePointer.type)) {
            this.endActiveHover();
        }

        for (const [pointerKey, pointerState] of this.pointerStates) {
            if (pointerState.releaseObserved)
                this.pointerStates.delete(pointerKey);
        }
    };

    private readonly handleCameraMoveStart = (): void => {
        this.navigationGeneration += 1;
        this.clearHover();
    };

    private readonly handleViewportDestroy = (): void => {
        const viewport = this.viewport;
        if (viewport) this.disconnect(viewport);
    };

    private createHoverSession(
        event: FederatedPointerEvent,
        target: InteractionTarget,
    ): HoverSession {
        return {
            target,
            pointer: getPointerIdentity(event),
            capturedOnHoverChange: target.onHoverChange,
        };
    }

    private canTriggerClick(pointerState: PointerState | undefined): boolean {
        return Boolean(
            this.viewport?.isUsable &&
                !this.viewport.isNavigating &&
                pointerState &&
                pointerState.navigationGenerationAtDown !== null &&
                pointerState.navigationGenerationAtDown ===
                    this.navigationGeneration &&
                !pointerState.clickSuppressed,
        );
    }

    private canActivateLongPress(
        pendingHover: PendingHoverState,
        pointerState: PointerState,
    ): boolean {
        const { session } = pendingHover;
        return Boolean(
            this.hoverState === pendingHover &&
                this.pointerStates.get(getPointerKey(session.pointer)) ===
                    pointerState &&
                pointerState.navigationGenerationAtDown !== null &&
                !pointerState.releaseObserved &&
                pointerState.navigationGenerationAtDown ===
                    this.navigationGeneration &&
                this.viewport?.isUsable &&
                !this.viewport.isNavigating,
        );
    }

    private ensurePointerState(pointer: PointerIdentity): PointerState {
        const pointerKey = getPointerKey(pointer);
        const existing = this.pointerStates.get(pointerKey);
        if (existing) return existing;

        const pointerState: PointerState = {
            navigationGenerationAtDown: null,
            releaseObserved: false,
            clickSuppressed: false,
        };
        this.pointerStates.set(pointerKey, pointerState);
        return pointerState;
    }

    private startImmediateHover(session: HoverSession): void {
        this.cancelPendingHover();
        if (
            this.isActiveHover({
                target: session.target,
                pointer: session.pointer,
            })
        ) {
            return;
        }

        this.endActiveHover();
        this.startHoverSession(session);
    }

    private startLongPress(
        session: HoverSession,
        pointerState: PointerState,
    ): void {
        this.cancelPendingHover();
        this.endActiveHover();

        const pendingHover: PendingHoverState = {
            type: "pending",
            session,
            timer: window.setTimeout(() => {
                if (!this.canActivateLongPress(pendingHover, pointerState)) {
                    if (this.hoverState === pendingHover)
                        this.hoverState = null;
                    return;
                }

                pointerState.clickSuppressed = true;
                this.startHoverSession(session);
            }, HOVER_LONG_PRESS_MS),
        };

        this.hoverState = pendingHover;
    }

    private startHoverSession(session: HoverSession): void {
        this.hoverState = { type: "active", session };
        session.capturedOnHoverChange?.(true);
    }

    private clearHover(filter: HoverSessionFilter = {}): void {
        this.cancelPendingHover(filter);
        this.endActiveHover(filter);
    }

    private cancelPendingHover(filter: HoverSessionFilter = {}): void {
        if (
            this.hoverState?.type !== "pending" ||
            !this.matchesHoverSession(this.hoverState.session, filter)
        ) {
            return;
        }

        window.clearTimeout(this.hoverState.timer);
        this.hoverState = null;
    }

    private endActiveHover(filter: HoverSessionFilter = {}): void {
        if (
            this.hoverState?.type !== "active" ||
            !this.matchesHoverSession(this.hoverState.session, filter)
        ) {
            return;
        }

        const session = this.hoverState.session;
        this.hoverState = null;
        session.capturedOnHoverChange?.(false);
    }

    private isActiveHover(filter: HoverSessionFilter = {}): boolean {
        return (
            this.hoverState?.type === "active" &&
            this.matchesHoverSession(this.hoverState.session, filter)
        );
    }

    private get activeHoverPointer(): PointerIdentity | null {
        return this.hoverState?.type === "active"
            ? this.hoverState.session.pointer
            : null;
    }

    private matchesHoverSession(
        session: HoverSession,
        { target, pointer }: HoverSessionFilter,
    ): boolean {
        return (
            (target === undefined || session.target === target) &&
            (pointer === undefined || samePointer(session.pointer, pointer))
        );
    }
}
